import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from "components/globals/ToastNotification";
import UserSelectionModal from "components/UserSelectionModal";
import TextEditor from 'components/forms/TextEditor';
import { triggerAlert } from 'components/globals/AlertNotification';

const Nonconformity = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [users, setUsers] = useState([]);
   const [toast, setToast] = useState(null);
   const [isEditing, setIsEditing] = useState(true);
   const [showModal, setShowModal] = useState(false);
   const [loading, setLoading] = useState(false); // State to manage loading

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;
   const user_id = decodedToken.user_id;

   const fetchNonconformityDetails = async () => {
      let data = null; // Declare a variable to store API response

      try {
         const response = await apiClient.get(`/nonconformities/${id}/`);
         data = response.data; // Store response data
         setFormData(data); // Update state
      } catch (error) {
         triggerAlert('Não Conformidade não foi encontrada!', 'error');
         navigate("/nonconformities");
      } finally {
         setIsEditing(data?.approved === null); // Use the local variable, NOT formData
      }
   };

   const handleCheckboxUsersChange = (userId, isChecked) => {
      setFormData((prevData) => {
         let updatedMembers = isChecked
            ? [...prevData.responsible_members, userId]
            : prevData.responsible_members.filter((id) => id !== userId);

         return { ...prevData, responsible_members: updatedMembers };
      });
   };


   useEffect(() => {
      const fetchUsers = async () => {
         try {
            const response = await apiClient.get('/users/active/');
            setUsers(response.data);
         } catch (error) {
            console.error('Error fetching users:', error);
         }
      };

      if (!id) {
         setFormData({ identified_by: user_id, responsible_members: [] });
         setIsEditing(true);
      } else {
         fetchNonconformityDetails();
      }
      fetchUsers();
   }, [id, location.pathname]);

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let updatedValue;

      if (type === "checkbox") {
         updatedValue = checked; // true if checked, false if unchecked
      } else if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      } else {
         updatedValue = value || null; // Convert empty values to null
      }

      setFormData((prevFormData) => {
         let updatedForm = {
            ...prevFormData,
            [name]: updatedValue,
         };

         if (name === "action" && !updatedValue) {
            updatedForm.action_description = "";
         }

         if (name === "approved" && updatedValue !== "") {
            updatedForm.handled_date = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
         }

         return updatedForm;
      });

      setIsEditing(true);
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true); // Set loading state to true
      try {
         if (id) {
            await apiClient.put(`/nonconformities/${id}/`, formData);
            triggerAlert('Não Conformidade foi atualizada com sucesso!', 'success');
            navigate(`/nonconformities`);
         } else {
            await apiClient.post('/nonconformities/', formData);
            triggerAlert('Não Conformidade foi criada com sucesso!', 'success');
            navigate('/nonconformities');
         }
      } catch (error) {
         const errorMessage = error.response?.data?.error || 'Erro ao gravar';
         setToast({ title: 'Não Conformidade', message: { errorMessage }, type: 'error' });
         setLoading(false); // Reset loading state on error
      }
   };

   if (formData !== null) {
      if (!isEditing && (formData.approved !== null || (user_id !== formData.identified_by && !is_director))) {
         return (

            <div className="container mt-4">
               <h4 className="text-center mb-5">Detalhes da Não Conformidade</h4>
               {[
                  { label: "Código", value: formData.code },
                  { label: "Descrição", value: formData.description },
                  {
                     label: "Identificada por",
                     value: formData.identified_by_name
                  },
                  { label: "Data de Registro", value: formData.created_at },
                  { label: "Origem", value: formData.origin },
                  { label: "O problema é generalizável?", value: formData.generalizable ? "Sim" : "Não" },
                  { label: "Causas", value: formData.causes },
                  { label: "Ação corretiva?", value: formData.action ? "Sim" : "Não" },
                  { label: "Descrição da ação corretiva", value: formData.action_description },
                  {
                     label: "Responsáveis",
                     value: formData.responsible_members.length > 0
                        ? formData.responsible_members
                           .map(id => users.find(user => user.id === id))
                           .map(user => `${user?.first_name} ${user?.last_name}`)
                           .join(", ")
                        : "Nenhum responsável selecionado"
                  },
                  { label: "Aprovado", value: formData.approved ? "Sim" : "Não" },
                  { label: "Data de Aprovação", value: formData.handled_date }
               ].map((item, index) => (
                  <CCard key={index} className="mb-3">
                     <CCardHeader>
                        <h5>{item.label}</h5>
                     </CCardHeader>
                     <CCardBody>
                        <div>{item.value || "-"}</div>
                     </CCardBody>
                  </CCard>
               ))}

               {/* Back button */}
               <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                  <CButton color="danger" className="text-white" onClick={() => navigate('/nao-conformidades')}>
                     Voltar
                  </CButton>
               </div>
            </div>
         );
      }

      return (
         <div className="container mt-4">
            {/* Error Toast - Appears on submit failure */}
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
               <CCardHeader>
                  <h4>{id ? 'Editar Não Conformidade' : 'Nova Não Conformidade'}</h4>
               </CCardHeader>
               <CCardBody>
                  <CForm onSubmit={handleSubmit}>
                     {/* Show "Código" field only when editing */}
                     {id && (
                        <CFormInput
                           type="text"
                           label="Código"
                           name="code"
                           value={formData.code}
                           readOnly
                           className="mb-3"
                        />
                     )}

                     <TextEditor
                        label="Descrição"
                        value={formData.description ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "description", value } })
                        }
                        className="mb-3"
                     />

                     <CFormSelect
                        label={<strong>Identificada por</strong>}
                        name="identified_by"
                        value={formData.identified_by || user_id} // Default to logged-in user
                        onChange={handleInputChange}
                        className="mb-3"
                        required={true}
                     >
                        <option value="">Selecione um colaborador</option>
                        {users.map((user) => (
                           <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                           </option>
                        ))}
                     </CFormSelect>

                     {is_director && (
                        <>
                           {id && (
                              <CFormInput
                                 type="text"
                                 label="Data registo"
                                 name="created_at"
                                 value={formData.created_at}
                                 readOnly
                                 className="mb-3"
                              />
                           )}

                           <CFormSelect
                              label={<strong>Origem</strong>}
                              name="origin"
                              value={formData.origin ?? ''}
                              onChange={handleInputChange}
                              className="mb-3"
                           >
                              <option value="">Selecione</option>
                              <option value="Auditoria Interna">Auditoria Interna</option>
                              <option value="Auditoria Externa">Auditoria Externa</option>
                              <option value="Auditorias Parciais">Auditorias Parciais</option>
                              <option value="Elaboração">Elaboração</option>
                              <option value="Gestão">Gestão</option>
                              <option value="S/ Incentivo">S/ Incentivo</option>
                              <option value="IDI">IDI</option>
                           </CFormSelect>

                           <div className="mt-3 mb-3">
                              <div><strong>O problema é generaliz&aacute;vel?</strong></div>
                              <CFormCheck
                                 name="generalizable"
                                 checked={formData.generalizable}
                                 onChange={handleInputChange}
                              />
                           </div>

                           <TextEditor
                              label="Causas"
                              value={formData.causes ?? ''}
                              onChange={(value) =>
                                 handleInputChange({ target: { name: "causes", value } })
                              }
                              className="mb-3"
                           />

                           <div className="mt-3 mb-3">
                              <div><strong>Ação corretiva?</strong></div>
                              <CFormCheck
                                 name="action"
                                 checked={formData.action}
                                 onChange={handleInputChange}
                              />
                           </div>

                           {formData.action && (
                              <TextEditor
                                 label="Se sim, qual?"
                                 value={formData.action_description ?? ''}
                                 onChange={(value) =>
                                    handleInputChange({ target: { name: "action_description", value } })
                                 }
                                 className="mb-3"
                              />
                           )}

                           <CButton color="info" className="text-white" onClick={() => setShowModal(true)}>
                              Selecionar Responsáveis
                           </CButton>

                           {/* Display selected users */}
                           <div className="mt-2">
                              {formData.responsible_members.length > 0 ? (
                                 <ul>
                                    {users
                                       .filter((user) => formData.responsible_members.includes(user.id))
                                       .map((user) => (
                                          <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                       ))}
                                 </ul>
                              ) : (
                                 <p className="text-muted">Nenhum responsável selecionado</p>
                              )}
                           </div>


                           <UserSelectionModal
                              title="Selecionar Responsáveis"
                              visible={showModal}
                              onClose={() => setShowModal(false)}
                              users={users}
                              selectedUsers={formData.responsible_members}
                              onChange={handleCheckboxUsersChange}
                           />

                           <CFormSelect
                              label={<strong>Aprovado</strong>}
                              name="approved"
                              value={formData.approved ?? ''}
                              onChange={handleInputChange}
                              className="mb-3"
                           >
                              <option value="">Selecione</option>
                              <option value="true">Sim</option>
                              <option value="false">Não</option>
                           </CFormSelect>

                           <DateInput
                              label={<strong>Data</strong>}
                              name="handled_date"
                              value={formData.handled_date ?? ''}
                              onChange={handleInputChange}
                           />
                        </>
                     )}
                  </CForm>
               </CCardBody>
               <CCardFooter className="d-flex justify-content-end gap-2">
                  <CButton color="danger" className="text-white" onClick={() => navigate("/nonconformities")}>
                     Cancelar
                  </CButton>
                  <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                     {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                  </CButton>
               </CCardFooter>
            </CCard>
         </div>
      );
   }

   return (
      <div className="container mt-4">
         <h2>Loading...</h2>
      </div>
   );
};

export default Nonconformity;
